/**
 * StrategyLibraryPage — browse, quicktest, and deploy pre-built trading strategies.
 * Supports automated strategy runner: shows deployed status, last run, and run history.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  Search, Star, Play, Rocket, Sliders, Tag, ChevronDown, ChevronUp,
  CheckCircle, Loader2, TrendingUp, TrendingDown, BarChart2,
  Square, RefreshCw, Clock, Zap, AlertCircle, Activity,
} from 'lucide-react'
import { api } from '@/lib/api'

// ── Types ──────────────────────────────────────────────────────────────────

interface Strategy {
  id: string
  name: string
  description: string
  category: string
  tags: string[]
  rules: Record<string, unknown>
  backtest_preset: Record<string, unknown>
  author: string
  rating: number
  deployable: boolean
}

// All numeric fields are null when the backtest engine is unavailable —
// the backend returns {total_return_pct: null, ..., error} rather than fakes.
interface BacktestResult {
  total_return_pct: number | null
  annualized_return_pct?: number | null
  max_drawdown_pct: number | null
  sharpe_ratio: number | null
  win_rate?: number | null
  total_trades?: number | null
  initial_capital: number
  final_value: number | null
  error?: string | null
}

interface SignalRecord {
  ticker: string
  action: 'BUY' | 'SELL'
  price: number
  date: string
  strategy: string
}

interface RunRecord {
  run_at: string
  tickers_checked: string[]
  signals_found: SignalRecord[]
  recommendations_created: number
  error: string | null
}

interface DeployedStrategy {
  deployment_id: string
  strategy_id: string
  strategy_name: string
  deployed_at: string
  status: 'active' | 'stopped'
  mode: string
  schedule_config: { frequency: string; active_hours: string }
  last_run_at: string | null
  last_signals: SignalRecord[]
  last_run: RunRecord | null
  run_count: number
}

// ── Constants ──────────────────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'long_term', label: 'Long Term' },
  { id: 'etfs', label: 'ETFs' },
  { id: 'investing', label: 'Investing' },
  { id: 'trading', label: 'Trading' },
  { id: 'technology', label: 'Technology' },
]

// ── Helpers ────────────────────────────────────────────────────────────────

function ratingStars(r: number) {
  const full = Math.floor(r)
  const half = r - full >= 0.3
  return (
    <span className="flex items-center gap-0.5 text-amber-400">
      {Array.from({ length: full }).map((_, i) => (
        <Star key={i} size={13} fill="currentColor" />
      ))}
      {half && <Star size={13} fill="currentColor" className="opacity-50" />}
      <span className="text-xs text-text-muted ml-1">{r.toFixed(1)}</span>
    </span>
  )
}

function formatRules(rules: Record<string, unknown>): string {
  const parts: string[] = []
  if (rules.action) parts.push(`THEN ${rules.action}`)
  if (rules.ticker) parts.push(`${rules.ticker}`)
  if (rules.tickers) parts.push(`${(rules.tickers as string[]).join(', ')}`)
  if (rules.condition) parts.push(`WHEN ${rules.condition}`)
  if (rules.frequency) parts.push(`every ${rules.frequency}`)
  if (rules.allocation_pct) parts.push(`${rules.allocation_pct}% allocation`)
  return parts.join(' | ')
}

/** snake_case / camelCase key → "Title Case" label. */
function humaniseKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase())
}

/** Render a single config value plainly: primitives as text, arrays and
 *  one level of nested object as compact chips. */
function ConfigValue({ value }: { value: unknown }) {
  if (value == null || value === '') return <span className="text-text-muted">—</span>
  if (typeof value === 'boolean') {
    return <span className={value ? 'text-emerald-400' : 'text-rose-400'}>{value ? 'Yes' : 'No'}</span>
  }
  if (Array.isArray(value)) {
    return (
      <span className="flex flex-wrap gap-1">
        {value.map((v, i) => (
          <span key={i} className="px-1.5 py-0.5 rounded bg-surface-hover border border-border text-text-secondary font-mono text-[11px]">
            {typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}
          </span>
        ))}
      </span>
    )
  }
  if (typeof value === 'object') {
    // Nested object — flatten one level into "Key: value" chips
    return (
      <span className="flex flex-wrap gap-1">
        {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
          <span key={k} className="px-1.5 py-0.5 rounded bg-surface-hover border border-border text-text-secondary text-[11px]">
            {humaniseKey(k)}:{' '}
            <span className="font-mono text-text-primary">
              {typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}
            </span>
          </span>
        ))}
      </span>
    )
  }
  return <span className="font-mono text-text-primary">{String(value)}</span>
}

/** Humanised label/value view of a config object, with the original raw JSON
 *  behind a collapsed "Raw configuration" disclosure for power users. */
function ConfigBlock({ data }: { data: object }) {
  const entries = Object.entries(data as Record<string, unknown>)
  return (
    <div>
      {entries.length === 0 ? (
        <p className="text-xs text-text-muted">No parameters</p>
      ) : (
        <div className="grid grid-cols-[minmax(100px,auto)_1fr] items-baseline gap-x-4 gap-y-1.5 text-xs bg-surface-DEFAULT p-3 rounded-lg border border-border">
          {entries.map(([k, v]) => (
            <div key={k} className="contents">
              <span className="text-text-muted">{humaniseKey(k)}</span>
              <ConfigValue value={v} />
            </div>
          ))}
        </div>
      )}
      <details className="mt-1.5">
        <summary className="text-[11px] text-text-muted cursor-pointer select-none hover:text-text-primary">
          Raw configuration
        </summary>
        <pre className="mt-1 text-[11px] text-text-muted font-mono bg-surface-DEFAULT p-3 rounded-lg border border-border overflow-x-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  )
}

function fmtDate(iso: string | null): string {
  if (!iso) return 'Never'
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// ── Run History Panel ──────────────────────────────────────────────────────

function RunHistoryPanel({ deploymentId }: { deploymentId: string }) {
  const [history, setHistory] = useState<RunRecord[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get(`/api/strategy-library/deployed/${deploymentId}/history`)
      .then(({ data }) => setHistory(data.history ?? []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false))
  }, [deploymentId])

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-xs text-text-muted">
        <Loader2 size={13} className="animate-spin" /> Loading history…
      </div>
    )
  }

  if (history.length === 0) {
    return (
      <p className="text-xs text-text-muted py-2">
        No runs yet. The strategy will run automatically every 15 minutes during market hours.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {history.slice(0, 8).map((run, i) => (
        <div
          key={i}
          className="flex items-start justify-between gap-3 p-2.5 rounded-lg bg-surface-DEFAULT border border-border text-xs"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Clock size={10} className="text-text-muted shrink-0" />
              <span className="text-text-muted">{fmtDate(run.run_at)}</span>
              {run.error ? (
                <span className="flex items-center gap-1 text-rose-400">
                  <AlertCircle size={10} /> Error
                </span>
              ) : (
                <span className="text-emerald-400">
                  {run.recommendations_created > 0
                    ? `${run.recommendations_created} rec(s)`
                    : 'No signals'}
                </span>
              )}
            </div>
            {run.signals_found.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {run.signals_found.map((sig, j) => (
                  <span
                    key={j}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono font-semibold ${
                      sig.action === 'BUY'
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'bg-rose-500/10 text-rose-400'
                    }`}
                  >
                    {sig.action === 'BUY'
                      ? <TrendingUp size={9} />
                      : <TrendingDown size={9} />}
                    {sig.action} {sig.ticker} @ ${sig.price}
                  </span>
                ))}
              </div>
            )}
            {run.error && (
              <p className="text-rose-400/80 mt-1 truncate">{run.error}</p>
            )}
          </div>
          <span className="text-text-muted shrink-0">
            {run.tickers_checked.length} ticker(s)
          </span>
        </div>
      ))}
    </div>
  )
}

// ── Strategy Card ──────────────────────────────────────────────────────────

function StrategyCard({
  strategy,
  deployed,
  onDeploy,
  onStop,
  onRun,
}: {
  strategy: Strategy
  deployed: DeployedStrategy | null
  onDeploy: (id: string) => void
  onStop: (id: string) => void
  onRun: (deploymentId: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [historyExpanded, setHistoryExpanded] = useState(false)
  const [testResult, setTestResult] = useState<BacktestResult | null>(null)
  const [testing, setTesting] = useState(false)
  const [deploying, setDeploying] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [running, setRunning] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const isDeployed = deployed !== null && deployed.status === 'active'

  const errText = (e: any) =>
    String(e?.response?.data?.detail ?? e?.message ?? 'unknown error')

  const handleTest = async () => {
    setTesting(true)
    setActionError(null)
    try {
      const { data } = await api.post(`/api/strategy-library/${strategy.id}/quicktest`)
      setTestResult(data?.backtest ?? null)
    } catch (e: any) {
      console.error('QuickTest failed:', e)
      setActionError(`QuickTest failed: ${errText(e)}`)
    }
    setTesting(false)
  }

  const handleDeploy = async () => {
    setDeploying(true)
    setActionError(null)
    try {
      await api.post(`/api/strategy-library/${strategy.id}/deploy`)
      onDeploy(strategy.id)
    } catch (e: any) {
      // A 409 is NOT success — show the backend's message instead of
      // pretending the deploy went through.
      console.error('Deploy failed:', e)
      setActionError(`Deploy failed: ${errText(e)}`)
    }
    setDeploying(false)
  }

  const handleStop = async () => {
    if (!deployed) return
    setStopping(true)
    setActionError(null)
    try {
      await api.post(`/api/strategy-library/${strategy.id}/undeploy`)
      onStop(strategy.id)
    } catch (e: any) {
      console.error('Stop failed:', e)
      setActionError(`Stop failed: ${errText(e)}`)
    }
    setStopping(false)
  }

  const handleRun = async () => {
    if (!deployed) return
    setRunning(true)
    setActionError(null)
    try {
      await api.post(`/api/strategy-library/deployed/${deployed.deployment_id}/run`)
      onRun(deployed.deployment_id)
    } catch (e: any) {
      console.error('Manual run failed:', e)
      setActionError(`Run failed: ${errText(e)}`)
    }
    setRunning(false)
  }

  // Engine-unavailable results have total_return_pct: null — never let null
  // read as a green "+0.0%" (null >= 0 is true in JS).
  const positive = testResult != null
    && testResult.total_return_pct != null
    && testResult.total_return_pct >= 0
  const lastSignals = deployed?.last_signals ?? []

  return (
    <div className={`card card-hover overflow-hidden ${
      isDeployed ? '!border-emerald-500/30' : ''
    }`}>
      {/* Header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-text-primary truncate">
                {strategy.name}
              </h3>
              {/* Running badge */}
              {isDeployed && (
                <span className="chip-live shrink-0">
                  Running
                </span>
              )}
            </div>
            <p className="text-xs text-text-muted mt-1 line-clamp-2">
              {strategy.description}
            </p>
          </div>
          <div className="shrink-0">{ratingStars(strategy.rating)}</div>
        </div>

        {/* Last run info for deployed strategies */}
        {isDeployed && (
          <div className="mt-2 flex items-center gap-3 text-[11px] text-text-muted">
            <span className="flex items-center gap-1">
              <Clock size={10} />
              Last run: {fmtDate(deployed?.last_run_at ?? null)}
            </span>
            {lastSignals.length > 0 && (
              <span className="flex items-center gap-1 text-emerald-400">
                <Zap size={10} />
                {lastSignals.length} signal(s)
              </span>
            )}
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {strategy.tags.map((tag) => (
            <span
              key={tag}
              className="badge-neutral inline-flex items-center gap-1"
            >
              <Tag size={9} />
              {tag}
            </span>
          ))}
        </div>

        {/* Rules */}
        <div className="mt-3 p-2.5 rounded-lg bg-surface-DEFAULT border border-border">
          <p className="text-[11px] font-mono text-text-muted truncate">
            {formatRules(strategy.rules)}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <button
            type="button"
            onClick={handleTest}
            disabled={testing}
            className="btn-secondary px-3 py-1.5 text-xs gap-1.5"
          >
            {testing ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            QuickTest
          </button>

          {isDeployed ? (
            <>
              {/* Manual run button */}
              <button
                type="button"
                onClick={handleRun}
                disabled={running}
                className="btn-primary px-3 py-1.5 text-xs gap-1.5"
                title="Manually trigger a strategy run now"
              >
                {running
                  ? <Loader2 size={13} className="animate-spin" />
                  : <RefreshCw size={13} />}
                Run Now
              </button>
              {/* Stop button */}
              <button
                type="button"
                onClick={handleStop}
                disabled={stopping}
                className="btn-secondary px-3 py-1.5 text-xs gap-1.5 !text-rose-400"
              >
                {stopping
                  ? <Loader2 size={13} className="animate-spin" />
                  : <Square size={13} />}
                Stop
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleDeploy}
              disabled={deploying || !strategy.deployable}
              className="btn-primary px-3 py-1.5 text-xs gap-1.5"
            >
              {deploying
                ? <Loader2 size={13} className="animate-spin" />
                : <Rocket size={13} />}
              Deploy
            </button>
          )}

          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="btn-ghost px-3 py-1.5 text-xs gap-1 ml-auto"
          >
            <Sliders size={13} />
            Customize
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        </div>
      </div>

      {/* Action error (deploy / stop / run / quicktest) */}
      {actionError && (
        <div className="px-5 pb-3">
          <p className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2 flex items-start gap-1.5">
            <AlertCircle size={12} className="shrink-0 mt-0.5" />
            {actionError}
          </p>
        </div>
      )}

      {/* QuickTest Result */}
      {testResult && testResult.error != null && (
        <div className="px-5 pb-4">
          <p className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
            QuickTest could not run: {testResult.error}
          </p>
        </div>
      )}
      {testResult && testResult.error == null && (
        <div className="px-5 pb-4">
          <div className="grid grid-cols-4 gap-3 p-3 rounded-lg bg-surface-DEFAULT border border-border">
            <div className="text-center">
              <p className="text-[10px] text-text-muted uppercase tracking-wider">Return</p>
              <p className="text-sm font-bold mt-0.5">
                {testResult.total_return_pct == null
                  ? <span className="text-text-muted">—</span>
                  : <span className={positive ? 'delta-chip-pos' : 'delta-chip-neg'}>
                      {positive ? '+' : ''}{testResult.total_return_pct.toFixed(1)}%
                    </span>}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-text-muted uppercase tracking-wider">Drawdown</p>
              <p className="text-sm font-bold text-rose-400 mt-0.5">
                {testResult.max_drawdown_pct == null ? '—' : `-${testResult.max_drawdown_pct.toFixed(1)}%`}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-text-muted uppercase tracking-wider">Sharpe</p>
              <p className="text-sm font-bold text-text-primary mt-0.5">
                {testResult.sharpe_ratio == null ? '—' : testResult.sharpe_ratio.toFixed(2)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-text-muted uppercase tracking-wider">Final</p>
              <p className="text-sm font-bold text-text-primary mt-0.5">
                {testResult.final_value == null ? '—' : `$${testResult.final_value.toLocaleString()}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Run History expandable (deployed only) */}
      {isDeployed && (
        <div className="border-t border-border">
          <button
            type="button"
            onClick={() => setHistoryExpanded(!historyExpanded)}
            className="w-full flex items-center justify-between px-5 py-3 text-xs text-text-muted hover:text-text-primary transition-colors"
          >
            <span className="flex items-center gap-1.5 font-medium">
              <Activity size={12} />
              Run History
              {deployed.run_count != null && deployed.run_count > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-surface-DEFAULT border border-border font-mono">
                  {deployed.run_count}
                </span>
              )}
            </span>
            {historyExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          {historyExpanded && (
            <div className="px-5 pb-4">
              <RunHistoryPanel deploymentId={deployed.deployment_id} />
            </div>
          )}
        </div>
      )}

      {/* Customize panel */}
      {expanded && (
        <div className={`px-5 pb-4 border-t border-border pt-4 ${isDeployed && !historyExpanded ? '' : ''}`}>
          <p className="text-xs text-text-muted mb-2 font-medium">Strategy Parameters</p>
          <ConfigBlock data={strategy.rules} />
          <p className="text-xs text-text-muted mt-3 mb-2 font-medium">Backtest Preset</p>
          <ConfigBlock data={strategy.backtest_preset} />
          {isDeployed && deployed.schedule_config && (
            <>
              <p className="text-xs text-text-muted mt-3 mb-2 font-medium">Schedule Config</p>
              <ConfigBlock data={deployed.schedule_config} />
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function StrategyLibraryPage() {
  const [strategies, setStrategies] = useState<Strategy[]>([])
  const [deployedMap, setDeployedMap] = useState<Record<string, DeployedStrategy>>({})
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('all')

  const fetchStrategies = useCallback(async () => {
    setLoading(true)
    setFetchError(null)
    try {
      const params: Record<string, string> = {}
      if (category !== 'all') params.category = category
      if (search.trim()) params.search = search.trim()
      const { data } = await api.get('/api/strategy-library', { params })
      setStrategies(data.strategies)
    } catch (e: any) {
      console.error('Failed to fetch strategies:', e)
      setFetchError(e?.message || 'Failed to load strategies. Please try again.')
    }
    setLoading(false)
  }, [category, search])

  const fetchDeployed = useCallback(async () => {
    try {
      const { data } = await api.get('/api/strategy-library/deployed/list')
      const map: Record<string, DeployedStrategy> = {}
      for (const d of data.deployed ?? []) {
        map[d.strategy_id] = d
      }
      setDeployedMap(map)
    } catch (e) {
      // Silently ignore — deployed list is non-critical
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(fetchStrategies, search ? 300 : 0)
    return () => clearTimeout(timer)
  }, [fetchStrategies, search])

  // Poll deployed list every 30 seconds to pick up auto-run results
  useEffect(() => {
    fetchDeployed()
    const interval = setInterval(fetchDeployed, 30_000)
    return () => clearInterval(interval)
  }, [fetchDeployed])

  const handleDeploy = useCallback((strategyId: string) => {
    // Refresh deployed list after deploy
    fetchDeployed()
  }, [fetchDeployed])

  const handleStop = useCallback((strategyId: string) => {
    // Mark stopped optimistically, then refresh
    setDeployedMap(prev => {
      const updated = { ...prev }
      if (updated[strategyId]) {
        updated[strategyId] = { ...updated[strategyId], status: 'stopped' }
      }
      return updated
    })
    fetchDeployed()
  }, [fetchDeployed])

  const handleRun = useCallback((_deploymentId: string) => {
    // Refresh deployed list after manual run to pick up new last_run_at
    setTimeout(fetchDeployed, 2000)
  }, [fetchDeployed])

  const activeCount = Object.values(deployedMap).filter(d => d.status === 'active').length

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <BarChart2 size={22} className="text-blue-light" />
            Strategy Library
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Browse, backtest, and deploy pre-built trading strategies.
            {activeCount > 0 && (
              <span className="chip-live ml-2">
                {activeCount} running
              </span>
            )}
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <input
            type="text"
            placeholder="Search strategies..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-9 py-2"
          />
        </div>
      </div>

      {/* Category tabs */}
      <div className="glass rounded-xl p-1 flex gap-1.5 overflow-x-auto">
        {CATEGORIES.map((c) => (
          <button
            type="button"
            key={c.id}
            onClick={() => setCategory(c.id)}
            className={`px-4 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap transition-colors ${
              category === c.id
                ? 'bg-blue text-white'
                : 'text-text-muted hover:text-text-primary hover:bg-surface-card'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Strategy Grid */}
      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton h-64" />
          ))}
        </div>
      ) : fetchError ? (
        <div className="text-center py-12">
          <p className="text-rose-400">{fetchError}</p>
          <button
            type="button"
            onClick={fetchStrategies}
            className="btn-secondary mt-4"
          >
            Retry
          </button>
        </div>
      ) : strategies.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-text-muted">No strategies found.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 stagger-children">
          {strategies.map((s) => (
            <StrategyCard
              key={s.id}
              strategy={s}
              deployed={deployedMap[s.id] ?? null}
              onDeploy={handleDeploy}
              onStop={handleStop}
              onRun={handleRun}
            />
          ))}
        </div>
      )}
    </div>
  )
}
